import { CONFIG } from '../config';
import { GameState } from '../core/Game';
import { Player } from '../entities/Player';
import { BulletSpawner } from '../systems/BulletSpawner';
import { OneWayBulletSpawner } from '../systems/OneWayBulletSpawner';
import { ScoreManager } from '../systems/ScoreManager';
import { CollisionDetector } from '../core/CollisionDetector';
import { ItemSpawner } from '../systems/ItemSpawner';
import { GameInputHandler } from './game/GameInputHandler';
import { PlayerStateManager } from './game/PlayerStateManager';
import { getInvertedColor } from '../utils/color';
import type { Item } from '../entities/Item';

// New systems
import { TimeManager } from '../systems/TimeManager';
import { ScreenShake } from '../systems/ScreenShake';
import { ComboManager } from '../systems/ComboManager';
import { PowerUpManager } from '../systems/PowerUpManager';
import { LaserSpawner } from '../systems/LaserSpawner';
import { BossManager } from '../systems/BossManager';
import { DangerWarning } from '../systems/DangerWarning';

// PixiJS rendering system
import {
    PixiRenderer,
    PixiGameRenderer,
    BulletAdapter,
    PlayerAdapter,
    ItemAdapter,
    LaserAdapter,
    BossAdapter,
    BossDeathEffectAdapter,
    ParticleAdapter
} from '../pixi';
import { speedLineTextureCache } from '../pixi/utils/SpeedLineTextureCache';

interface RendererLike {
    getCanvasWidth(): number;
    getCanvasHeight(): number;
    isLandscape: boolean;
}

interface SoundManagerLike {
    playBGM(): Promise<void>;
    getCurrentTrackTitle(): string | null;
    getAudioIntensity(): number;
    playItemSpawnSound(): void;
    playNearMissSound(): void;
    playComboSound(combo: number): void;
    playPowerUpSound(): void;
    playBombSound(): void;
    playBossWarning(): void;
    playBossDefeatSound(): void;
    playEvolveSound(): void;
    playDevolveSound(): void;
    switchToBossBGM(): Promise<void>;
    switchToNormalBGM(): Promise<void>;
}

interface InputManagerLike {
    isButtonPressed(button: string): boolean;
}

interface StatsManagerLike {
    startSession(difficulty: string, bigballMode?: boolean): void;
    updateTime(deltaTime: number): void;
    endSession(): void;
    getStatsForAchievements(): unknown;
    recordBossDefeat(): void;
    session: {
        survivalTime: number;
        score: number;
    };
}

interface AchievementManagerLike {
    clearNewlyUnlocked(): void;
    checkAchievements(stats: unknown): void;
}

interface BackgroundState {
    hue: number;
    angle: number;
}

interface Options {
    difficulty: string;
    bigballMode: boolean;
    bgStyle: string;
}

interface GameLike {
    getRenderer(): RendererLike;
    getSoundManager(): SoundManagerLike;
    getInputManager(): InputManagerLike;
    getStatsManager(): StatsManagerLike;
    getAchievementManager(): AchievementManagerLike;
    getBackgroundState(): BackgroundState;
    getOptions(): Options;
    setState(state: GameState): void;
}

interface ButtonArea {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface ScreenState {
    isPaused: boolean;
    isGameOver: boolean;
    showGameOverUI: boolean;
    buttonClickHandled: boolean;
    restartButton: ButtonArea;
    titleButton: ButtonArea;
    pauseTitleButton: ButtonArea;
    pauseContinueButton: ButtonArea;
    onResume: () => void;
    onRestart: () => void;
    onTitle: () => void;
}

type GameOverButtonSelection = 'restart' | 'title';
type PauseButtonSelection = 'continue' | 'title';

export class GameScreen {
    private game: GameLike;
    private player: Player;
    private bulletSpawner: BulletSpawner;
    private oneWayBulletSpawner: OneWayBulletSpawner;
    private itemSpawner: ItemSpawner;
    private scoreManager: ScoreManager;
    private isGameOver: boolean;

    // 게임오버 딜레이
    private gameOverDelay: number;
    private showGameOverUI: boolean;

    // UI 버튼 바운드
    private restartButton: ButtonArea;
    private titleButton: ButtonArea;
    private buttonClickHandled: boolean;

    // 스피드 라인
    private speedLineOffset: number;

    // 일시정지 상태
    private isPaused: boolean;
    private pauseTitleButton: ButtonArea;
    private pauseContinueButton: ButtonArea;
    private selectedPauseButton: PauseButtonSelection;

    // 게임오버 UI 상태
    private starAngle: number;
    private selectedButton: GameOverButtonSelection;

    // 노래 제목 표시 상태
    private trackTitleTimer: number;
    private trackTitleOpacity: number;
    private trackTitle: string | null;

    // 분리된 모듈들
    private playerState: PlayerStateManager;
    private inputHandler: GameInputHandler;
    private screenState: ScreenState;

    // 새 시스템들
    private timeManager: TimeManager;
    private screenShake: ScreenShake;
    private comboManager: ComboManager;
    private powerUpManager: PowerUpManager;
    private laserSpawner: LaserSpawner;
    private bossManager: BossManager;
    private dangerWarning: DangerWarning;

    // 보스 테스트 모드
    private bossTestMode: boolean = false;
    private lastDeltaTime: number = 0;

    // PixiJS 렌더링 시스템
    private pixiRenderer: PixiRenderer | null = null;
    private pixiGameRenderer: PixiGameRenderer | null = null;
    private bulletAdapter: BulletAdapter | null = null;
    private playerAdapter: PlayerAdapter | null = null;
    private itemAdapter: ItemAdapter | null = null;
    private laserAdapter: LaserAdapter | null = null;
    private bossAdapter: BossAdapter | null = null;
    private bossDeathEffectAdapter: BossDeathEffectAdapter | null = null;
    private particleAdapter: ParticleAdapter | null = null;
    private pixiInitialized: boolean = false;

    constructor(game: GameLike) {
        this.game = game;
        this.player = new Player();
        this.bulletSpawner = new BulletSpawner(game.getSoundManager());
        this.bulletSpawner.setRenderer(game.getRenderer());
        this.oneWayBulletSpawner = new OneWayBulletSpawner();
        this.oneWayBulletSpawner.setRenderer(game.getRenderer());
        this.itemSpawner = new ItemSpawner(game.getSoundManager());
        this.itemSpawner.setRenderer(game.getRenderer());
        this.scoreManager = new ScoreManager();
        this.isGameOver = false;

        // 게임오버 딜레이
        this.gameOverDelay = 0;
        this.showGameOverUI = false;

        // UI 버튼 바운드
        this.restartButton = { x: 0, y: 0, width: 0, height: 0 };
        this.titleButton = { x: 0, y: 0, width: 0, height: 0 };
        this.buttonClickHandled = false;

        // 스피드 라인
        this.speedLineOffset = 0;

        // 일시정지 상태
        this.isPaused = false;
        this.pauseTitleButton = { x: 0, y: 0, width: 0, height: 0 };
        this.pauseContinueButton = { x: 0, y: 0, width: 0, height: 0 };
        this.selectedPauseButton = 'continue'; // 기본 선택: continue

        // 게임오버 UI 상태
        this.starAngle = 0;
        this.selectedButton = 'restart'; // 기본 선택: restart

        // 노래 제목 표시 상태
        this.trackTitleTimer = 0;
        this.trackTitleOpacity = 0;
        this.trackTitle = null;

        // 분리된 모듈들
        this.playerState = new PlayerStateManager(game as never, this.player);

        // 새 시스템들 초기화
        const renderer = game.getRenderer();
        this.timeManager = new TimeManager();
        this.screenShake = new ScreenShake();
        this.comboManager = new ComboManager();
        this.powerUpManager = new PowerUpManager();
        this.laserSpawner = new LaserSpawner();
        this.laserSpawner.setRenderer(renderer);
        this.bossManager = new BossManager(renderer.getCanvasWidth(), renderer.getCanvasHeight());
        this.bossManager.setRenderer(renderer);
        this.dangerWarning = new DangerWarning();

        // 보스 콜백 설정
        this.bossManager.setOnBossSpawn(() => {
            game.getSoundManager().playBossWarning();
            this.screenShake.shakeStrong();
        });
        this.bossManager.setOnBossDefeat(() => {
            game.getSoundManager().playBossDefeatSound();
            this.screenShake.shake();
            this.timeManager.triggerSlowMo();
            // 첫 보스 처치 후 레이저 활성화
            this.laserSpawner.enable();
            // 보스 처치 통계 기록
            game.getStatsManager().recordBossDefeat();
        });

        // 폭탄 효과 콜백 설정
        this.powerUpManager.setOnBombActivated(() => {
            this.screenShake.shakeStrong();
            this.timeManager.triggerSlowMo(0.3, 0.2);
        });

        // 입력 핸들러에 전달할 상태 객체
        const screenState: ScreenState = {
            isPaused: false,
            isGameOver: false,
            showGameOverUI: false,
            buttonClickHandled: false,
            restartButton: this.restartButton,
            titleButton: this.titleButton,
            pauseTitleButton: this.pauseTitleButton,
            pauseContinueButton: this.pauseContinueButton,
            onResume: () => this.resumeGame(),
            onRestart: () => this.init(),
            onTitle: () => this.game.setState(GameState.TITLE)
        };
        this.screenState = screenState;
        this.inputHandler = new GameInputHandler(game as never, screenState);
    }

    init(): void {
        this.player.reset();

        const renderer = this.game.getRenderer();
        this.player.x = renderer.getCanvasWidth() / 2;
        this.player.y = renderer.getCanvasHeight() / 2;

        // 난이도 설정 적용
        const options = this.game.getOptions();
        const difficultyKey = options.difficulty as keyof typeof CONFIG.DIFFICULTY_SETTINGS;
        const difficultySettings = CONFIG.DIFFICULTY_SETTINGS[difficultyKey] || CONFIG.DIFFICULTY_SETTINGS.normal;
        this.bulletSpawner.setDifficultyMultipliers(difficultySettings);

        // 빅볼 모드 적용
        this.bulletSpawner.setBigballMode(options.bigballMode);
        this.oneWayBulletSpawner.setBigballMode(options.bigballMode);

        // 점수 배율 설정 (빅볼 모드일 때 추가 2배)
        let scoreMult = difficultySettings.SCORE_MULT;
        if (options.bigballMode) {
            scoreMult *= CONFIG.BIGBALL.SCORE_MULT;
        }
        this.scoreManager.setScoreMultiplier(scoreMult);

        this.bulletSpawner.reset();
        this.oneWayBulletSpawner.reset();
        this.itemSpawner.reset();
        this.scoreManager.reset();
        this.isGameOver = false;

        // 새 시스템들 리셋
        this.timeManager.reset();
        this.screenShake.reset();
        this.comboManager.reset();
        this.powerUpManager.reset();
        this.laserSpawner.reset();
        this.bossManager.reset();
        this.dangerWarning.reset();

        this.gameOverDelay = 0;
        this.showGameOverUI = false;
        this.speedLineOffset = 0;
        this.isPaused = false;
        this.starAngle = 0;
        this.selectedButton = 'restart';
        this.selectedPauseButton = 'continue';

        this.playerState.reset();
        this.syncScreenState();

        // PixiJS 렌더러 및 어댑터 초기화
        // 게임 내 리스타트: clear() 호출 (Graphics 정리)
        // 화면 전환 후: clearAll()로 이미 제거되었으므로 reset()으로 참조만 정리
        if (this.pixiRenderer && this.pixiRenderer.isInitialized()) {
            const layers = this.pixiRenderer.getLayers();
            // 컨테이너에 자식이 있으면 게임 내 리스타트, 없으면 화면 전환 후
            const isInGameRestart = layers.bullets.children.length > 0 ||
                                    layers.player.children.length > 0 ||
                                    layers.background.children.length > 0;

            if (this.pixiGameRenderer) {
                if (isInGameRestart) {
                    this.pixiGameRenderer.clear();
                }
                this.pixiGameRenderer.reset();
            }
            if (this.playerAdapter) {
                if (isInGameRestart) this.playerAdapter.clear();
                this.playerAdapter.reset();
            }
            if (this.bulletAdapter) {
                if (isInGameRestart) this.bulletAdapter.clear();
                this.bulletAdapter.reset();
            }
            if (this.itemAdapter) {
                if (isInGameRestart) this.itemAdapter.clear();
                this.itemAdapter.reset();
            }
            if (this.laserAdapter) {
                if (isInGameRestart) this.laserAdapter.clear();
                this.laserAdapter.reset();
            }
            if (this.bossAdapter) {
                if (isInGameRestart) this.bossAdapter.clear();
                this.bossAdapter.reset();
            }
            if (this.particleAdapter) {
                if (isInGameRestart) this.particleAdapter.clear();
                this.particleAdapter.reset();
            }
        }

        // 노래 제목 표시 초기화
        this.trackTitleTimer = 0;
        this.trackTitleOpacity = 0;
        this.trackTitle = null;

        // 선택된 BGM 재생
        const soundManager = this.game.getSoundManager();
        soundManager.playBGM().then(() => {
            this.trackTitle = soundManager.getCurrentTrackTitle();
        });

        // Start stats tracking session
        const statsManager = this.game.getStatsManager();
        statsManager.startSession(options.difficulty, options.bigballMode);

        // Clear newly unlocked achievements from previous game
        this.game.getAchievementManager().clearNewlyUnlocked();

        // URL 파라미터로 보스 테스트 모드 확인 (?boss 또는 #boss)
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = window.location.hash.substring(1);

        // BossManager에 가로/세로 모드 설정
        this.bossManager.setLandscape(renderer.isLandscape);

        if (urlParams.has('boss') || hashParams === 'boss') {
            this.bossTestMode = true;
            this.bossManager.setTestMode(true);
            // 즉시 보스 스폰
            this.bossManager.forceSpawnBoss();
            // 테스트용: 3개 파워업 활성화
            this.powerUpManager.activateLightning();
            this.powerUpManager.activateClock();
            this.powerUpManager.activateMagnet();
        } else {
            this.bossTestMode = false;
            this.bossManager.setTestMode(false);
        }
    }

    syncScreenState(): void {
        this.screenState.isPaused = this.isPaused;
        this.screenState.isGameOver = this.isGameOver;
        this.screenState.showGameOverUI = this.showGameOverUI;
    }

    setPixiRenderer(renderer: PixiRenderer): void {
        if (this.pixiInitialized) return;

        try {
            this.pixiRenderer = renderer;
            const layers = this.pixiRenderer.getLayers();

            // PixiGameRenderer 초기화
            this.pixiGameRenderer = new PixiGameRenderer(
                this.game as never,
                layers
            );

            // 어댑터들 초기화
            this.bulletAdapter = new BulletAdapter(
                layers.getLayer('bullets'),
                layers.getLayer('oneWayBullets')
            );
            this.playerAdapter = new PlayerAdapter(layers.getLayer('player'));
            this.itemAdapter = new ItemAdapter(layers.getLayer('items'));
            this.laserAdapter = new LaserAdapter(layers.getLayer('lasers'));
            this.bossAdapter = new BossAdapter(layers.getLayer('bosses'));
            this.bossDeathEffectAdapter = new BossDeathEffectAdapter(layers.getLayer('bosses'));
            this.particleAdapter = new ParticleAdapter(layers.getLayer('particles'));

            this.pixiInitialized = true;
        } catch (error) {
            console.error('Failed to initialize PixiJS adapters:', error);
            this.pixiInitialized = false;
        }
    }

    onOrientationChange(isLandscape: boolean, wasLandscape: boolean): void {
        if (!this.isGameOver) {
            // Transform player position
            this.transformPlayerPosition(wasLandscape, isLandscape);

            // Transform all entities
            this.bulletSpawner.transformPositionsOnOrientationChange(wasLandscape, isLandscape);
            this.oneWayBulletSpawner.transformPositionsOnOrientationChange(wasLandscape, isLandscape);
            this.itemSpawner.transformPositionsOnOrientationChange(wasLandscape, isLandscape);
            this.bossManager.transformPositionsOnOrientationChange(wasLandscape, isLandscape);
            this.laserSpawner.transformPositionsOnOrientationChange(wasLandscape, isLandscape);

            // Clear background graphics for new dimensions
            if (this.pixiGameRenderer) {
                this.pixiGameRenderer.clearBackgroundGraphics();
            }

            // 버튼 영역 초기화 (다음 render에서 재계산됨)
            // 참조를 유지하면서 값만 초기화
            this.pauseTitleButton.x = 0;
            this.pauseTitleButton.y = 0;
            this.pauseTitleButton.width = 0;
            this.pauseTitleButton.height = 0;
            this.pauseContinueButton.x = 0;
            this.pauseContinueButton.y = 0;
            this.pauseContinueButton.width = 0;
            this.pauseContinueButton.height = 0;

            this.isPaused = true;
            this.syncScreenState();
        }
    }

    transformPlayerPosition(wasLandscape: boolean, isLandscape: boolean): void {
        // Get old and new canvas sizes
        const oldWidth = wasLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
        const oldHeight = wasLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;
        const newWidth = isLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
        const newHeight = isLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;

        // Normalize position (0-1 range)
        const normalizedX = this.player.x / oldWidth;
        const normalizedY = this.player.y / oldHeight;

        // Rotate 90 degrees based on orientation change direction
        if (wasLandscape && !isLandscape) {
            // Landscape → Portrait
            this.player.x = normalizedY * newWidth;
            this.player.y = (1 - normalizedX) * newHeight;
        } else {
            // Portrait → Landscape
            this.player.x = (1 - normalizedY) * newWidth;
            this.player.y = normalizedX * newHeight;
        }

        // Clamp player position to stay within play area
        const margin = CONFIG.PLAY_AREA.MARGIN + this.player.radius;
        this.player.x = Math.max(margin, Math.min(newWidth - margin, this.player.x));
        this.player.y = Math.max(margin, Math.min(newHeight - margin, this.player.y));
    }

    resumeGame(): void {
        this.isPaused = false;
        this.syncScreenState();
    }

    update(deltaTime: number): void {
        this.lastDeltaTime = deltaTime;
        this.syncScreenState();

        if (this.isPaused) {
            this.updatePauseUI(deltaTime);
            return;
        }

        const input = this.game.getInputManager();
        const soundManager = this.game.getSoundManager();

        // TimeManager로 스케일된 deltaTime 얻기
        const scaledDelta = this.timeManager.update(deltaTime);

        // 화면 흔들림 업데이트 (원본 deltaTime 사용)
        this.screenShake.update(deltaTime);

        this.updateSpeedLines(scaledDelta);
        this.updateTrackTitle(deltaTime); // 트랙 타이틀은 원본 시간 사용
        this.playerState.update(scaledDelta);

        // 게임오버 처리
        if (this.isGameOver) {
            this.updateGameOver(deltaTime, input);
            return;
        }

        // 플레이어 업데이트 (파워업 속도 배율 적용)
        const renderer = this.game.getRenderer();
        const playerSpeedMult = this.powerUpManager.getSpeedMultiplier();
        this.player.update(scaledDelta * playerSpeedMult, input as never, renderer.getCanvasWidth(), renderer.getCanvasHeight());

        // 게임 시간
        const gameTime = this.scoreManager.getRawTime();

        // 총알 속도 배율 (파워업)
        const bulletSpeedMult = this.powerUpManager.getBulletSpeedMultiplier();

        // 스포너들 업데이트 (시계 효과는 총알과 단방향 총알 모두에 적용)
        this.bulletSpawner.update(scaledDelta, bulletSpeedMult);
        this.oneWayBulletSpawner.update(scaledDelta, bulletSpeedMult);
        this.itemSpawner.update(scaledDelta);
        this.scoreManager.update(scaledDelta);

        // 새 시스템들 업데이트
        this.comboManager.update(scaledDelta);
        this.powerUpManager.update(scaledDelta);
        this.laserSpawner.update(scaledDelta, gameTime);
        this.bossManager.update(scaledDelta, gameTime, this.player.x, this.player.y);

        // 보스 총알 수집 및 메인 총알 스포너에 추가
        const bossBullets = this.bossManager.collectBullets();
        for (const bullet of bossBullets) {
            this.bulletSpawner.addBullet(bullet);
        }

        // 자석 효과
        this.powerUpManager.applyMagnetEffect(
            this.player.x,
            this.player.y,
            this.itemSpawner.getItems(),
            scaledDelta
        );

        // 위험 경고 업데이트
        const allBullets = this.bulletSpawner.getBullets();
        this.dangerWarning.update(this.player, allBullets);

        // Near Miss 체크 (총알 + 단방향 총알) - 콤보만 카운트
        const allOneWayBullets = this.oneWayBulletSpawner.getBullets();
        this.comboManager.checkNearMiss(
            { x: this.player.x, y: this.player.y, radius: this.player.radius },
            allBullets,
            allOneWayBullets
        );

        // 콤보 배율 적용
        this.scoreManager.setComboMultiplier(this.comboManager.getMultiplier());

        // Update stats manager with time
        this.game.getStatsManager().updateTime(scaledDelta);

        // 충돌 처리
        if (!this.playerState.isInvincible()) {
            if (this.checkBulletCollisions() || this.checkOneWayBulletCollisions() || this.checkLaserCollisions()) {
                return;
            }
        }

        this.checkItemCollisions();
    }

    updateSpeedLines(deltaTime: number): void {
        // 현재 패턴의 타일 높이를 리셋 간격으로 사용
        const options = this.game.getOptions();
        const bgStyle = options.bgStyle as 'stripe' | 'diamond' | 'triangle' | 'sine' | 'hexagon' | 'grid';
        const tileSize = speedLineTextureCache.getTileSize(bgStyle);
        const resetInterval = tileSize.height;

        this.speedLineOffset += CONFIG.GAME_SCREEN.SPEED_LINE_SPEED * deltaTime;
        if (this.speedLineOffset >= resetInterval) {
            this.speedLineOffset -= resetInterval;
        }
    }

    updateTrackTitle(deltaTime: number): void {
        if (!this.trackTitle) return;

        this.trackTitleTimer += deltaTime;

        // 0-0.5초: 페이드 인
        // 0.5-2.5초: 유지
        // 2.5-3초: 페이드 아웃
        // 3초 이후: 숨김
        if (this.trackTitleTimer < 0.5) {
            this.trackTitleOpacity = this.trackTitleTimer / 0.5;
        } else if (this.trackTitleTimer < 2.5) {
            this.trackTitleOpacity = 1;
        } else if (this.trackTitleTimer < 3) {
            this.trackTitleOpacity = 1 - (this.trackTitleTimer - 2.5) / 0.5;
        } else {
            this.trackTitleOpacity = 0;
            this.trackTitle = null;
        }
    }

    updatePauseUI(deltaTime: number): void {
        this.starAngle += deltaTime * 3;
        const input = this.game.getInputManager();

        // 좌우 키로 버튼 선택
        if (input.isButtonPressed('left') || input.isButtonPressed('up')) {
            this.selectedPauseButton = 'title';
        }
        if (input.isButtonPressed('right') || input.isButtonPressed('down')) {
            this.selectedPauseButton = 'continue';
        }

        // Confirm으로 선택 실행
        if (input.isButtonPressed('confirm')) {
            if (this.selectedPauseButton === 'continue') {
                this.resumeGame();
            } else {
                this.game.setState(GameState.TITLE);
            }
        }
        // Cancel은 항상 resume
        if (input.isButtonPressed('cancel')) {
            this.resumeGame();
        }
    }

    updateGameOver(deltaTime: number, input: InputManagerLike): void {
        this.gameOverDelay += deltaTime;
        this.starAngle += deltaTime * 3;

        // 총알 업데이트 (30초까지만 스폰)
        if (this.gameOverDelay < CONFIG.GAME_SCREEN.BULLET_STOP_TIME) {
            this.bulletSpawner.update(deltaTime);
        } else {
            this.bulletSpawner.updateBulletsOnly(deltaTime);
        }
        this.oneWayBulletSpawner.update(deltaTime);

        if (this.gameOverDelay >= CONFIG.GAME_SCREEN.GAME_OVER_DELAY) {
            this.showGameOverUI = true;
            this.syncScreenState();

            if (!this.screenState.buttonClickHandled) {
                // 좌우 키로 버튼 선택
                if (input.isButtonPressed('left') || input.isButtonPressed('up')) {
                    this.selectedButton = 'title';
                }
                if (input.isButtonPressed('right') || input.isButtonPressed('down')) {
                    this.selectedButton = 'restart';
                }

                // Confirm으로 선택 실행
                if (input.isButtonPressed('confirm')) {
                    if (this.selectedButton === 'restart') {
                        this.init();
                    } else {
                        this.game.setState(GameState.TITLE);
                    }
                }
                // Cancel은 항상 Title로
                if (input.isButtonPressed('cancel')) {
                    this.game.setState(GameState.TITLE);
                }
            }
            this.screenState.buttonClickHandled = false;
        }
    }

    checkBulletCollisions(): boolean {
        // 보스 테스트 모드에서는 무적
        if (this.bossTestMode) return false;

        const bullets = this.bulletSpawner.getBullets();
        if (CollisionDetector.checkPlayerBulletCollisions(this.player, bullets)) {
            if (!this.playerState.handleHit()) {
                this.gameOver();
                return true;
            }
        }
        return false;
    }

    checkOneWayBulletCollisions(): boolean {
        // 보스 테스트 모드에서는 무적
        if (this.bossTestMode) return false;

        const oneWayBullets = this.oneWayBulletSpawner.getBullets();
        for (const bullet of oneWayBullets) {
            if (bullet.checkCollisionWithPlayer(this.player)) {
                if (!this.playerState.handleHit()) {
                    this.gameOver();
                    return true;
                }
                break;
            }
        }
        return false;
    }

    checkLaserCollisions(): boolean {
        // 보스 테스트 모드에서는 무적
        if (this.bossTestMode) return false;

        if (this.laserSpawner.checkCollisions(this.player)) {
            if (!this.playerState.handleHit()) {
                this.gameOver();
                return true;
            }
        }
        return false;
    }

    checkItemCollisions(): void {
        const items = this.itemSpawner.getItems();
        const collectedItems = CollisionDetector.checkPlayerItemCollisions(this.player, items);
        const soundManager = this.game.getSoundManager();

        for (const item of collectedItems as Item[]) {
            if (item.type === 'heart') {
                this.playerState.collectHeart();
            } else if (item.type === 'star') {
                this.playerState.collectStar();
            } else if (item.type === 'lightning') {
                this.powerUpManager.activateLightning();
                soundManager.playPowerUpSound();
            } else if (item.type === 'clock') {
                this.powerUpManager.activateClock();
                soundManager.playPowerUpSound();
            } else if (item.type === 'bomb') {
                this.powerUpManager.activateBomb(this.bulletSpawner, this.oneWayBulletSpawner);
                soundManager.playBombSound();
            } else if (item.type === 'magnet') {
                this.powerUpManager.activateMagnet();
                soundManager.playPowerUpSound();
            }
            this.itemSpawner.removeItem(item);
        }
    }

    render(): void {
        // PixiJS 렌더링만 사용
        if (!this.pixiInitialized || !this.pixiRenderer || !this.pixiGameRenderer) {
            return;
        }

        const bg = this.game.getBackgroundState();
        const options = this.game.getOptions();
        const soundManager = this.game.getSoundManager();
        const audioIntensity = soundManager.getAudioIntensity();

        // 배경 렌더링
        this.pixiGameRenderer.renderBackground(bg.hue, bg.angle);
        this.pixiGameRenderer.renderSpeedLines(
            this.speedLineOffset,
            options.bgStyle as 'stripe' | 'diamond' | 'triangle' | 'sine' | 'hexagon' | 'grid'
        );
        this.pixiGameRenderer.renderPlayAreaBorder(audioIntensity);

        // 화면 흔들림 적용
        const shakeOffset = this.screenShake.getOffset();
        this.pixiRenderer.getLayers().applyScreenShake(shakeOffset.x, shakeOffset.y);

        // 엔티티 렌더링 (어댑터 사용)
        const renderer = this.game.getRenderer();
        const canvasWidth = renderer.getCanvasWidth();
        const canvasHeight = renderer.getCanvasHeight();

        if (this.playerAdapter) {
            if (!this.isGameOver) {
                const playerOpacity = 0.6 + audioIntensity * 0.4;
                const playerLineWidth = 1 + audioIntensity * 0.5;
                this.playerAdapter.sync(this.player, getInvertedColor(), playerOpacity, playerLineWidth);
            } else {
                this.playerAdapter.hide();
            }
        }

        if (this.bulletAdapter) {
            this.bulletAdapter.syncBullets(this.bulletSpawner.getBullets());
            this.bulletAdapter.syncOneWayBullets(this.oneWayBulletSpawner.getBullets());
        }

        if (this.laserAdapter) {
            this.laserAdapter.sync(this.laserSpawner.getLasers(), canvasWidth, canvasHeight);
        }

        if (this.bossAdapter) {
            this.bossAdapter.sync(this.bossManager.getBosses());
        }

        if (this.bossDeathEffectAdapter) {
            this.bossDeathEffectAdapter.sync(this.bossManager.getDeathEffects());
        }

        if (this.itemAdapter) {
            this.itemAdapter.sync(this.itemSpawner.getItems(), audioIntensity);
        }

        if (this.particleAdapter) {
            this.particleAdapter.sync(this.playerState.getParticles());
        }

        // UI 렌더링
        this.pixiGameRenderer.renderScoreUI(this.scoreManager.getFormattedTime());

        // 콤보 UI
        const combo = this.comboManager.getCombo();
        if (combo > 0) {
            this.pixiGameRenderer.renderComboUI(
                combo,
                this.comboManager.getMultiplier(),
                this.comboManager.getComboTimerRatio()
            );
        }

        // 보스 체력 바
        const mainBoss = this.bossManager.getMainBoss();
        if (mainBoss) {
            this.pixiGameRenderer.renderBossHealthBar(
                mainBoss.health,
                mainBoss.maxHealth,
                mainBoss.getName()
            );
        }

        // 활성 파워업 표시
        const activePowerUps = this.powerUpManager.getActivePowerUps();
        this.pixiGameRenderer.renderPowerUpUI(activePowerUps);

        // 게임오버/일시정지 UI
        if (this.showGameOverUI) {
            this.pixiGameRenderer.renderGameOverUI(
                this.scoreManager,
                {
                    restart: this.restartButton,
                    title: this.titleButton
                },
                this.starAngle,
                this.selectedButton
            );
        }

        if (this.isPaused && !this.isGameOver) {
            this.pixiGameRenderer.renderPauseUI(
                {
                    title: this.pauseTitleButton,
                    continue: this.pauseContinueButton
                },
                this.starAngle,
                this.selectedPauseButton
            );
        } else {
            this.pixiGameRenderer.hidePauseUI();
        }

        // 보스 테스트 모드: FPS 표시
        if (this.bossTestMode) {
            this.pixiGameRenderer.renderFPS(this.lastDeltaTime);
        } else {
            this.pixiGameRenderer.hideFPS();
        }

        // PixiJS 렌더링 실행
        this.pixiRenderer.render();
    }

    gameOver(): void {
        this.isGameOver = true;
        this.gameOverDelay = 0;
        this.showGameOverUI = false;
        this.scoreManager.saveHighScore();
        this.syncScreenState();

        // End stats session and check achievements
        const statsManager = this.game.getStatsManager();
        statsManager.session.survivalTime = this.scoreManager.getRawTime();  // 실제 생존 시간
        statsManager.session.score = this.scoreManager.getScore();  // 난이도 배율 적용된 점수
        statsManager.endSession();

        // Check achievements with current stats
        this.game.getAchievementManager().checkAchievements(statsManager.getStatsForAchievements());
    }

    cleanup(): void {
        // 입력 핸들러 정리 (이벤트 리스너 제거)
        this.inputHandler.cleanup();

        // 스포너들 리셋 (배열 정리)
        this.bulletSpawner.reset();
        this.oneWayBulletSpawner.reset();
        this.itemSpawner.reset();

        // 시스템들 리셋
        this.timeManager.reset();
        this.screenShake.reset();
        this.comboManager.reset();
        this.powerUpManager.reset();
        this.laserSpawner.reset();
        this.bossManager.reset();
        this.dangerWarning.reset();

        // 플레이어 상태 리셋
        this.playerState.reset();

        // PixiJS 어댑터들 정리
        if (this.bulletAdapter) {
            this.bulletAdapter.clear();
        }
        if (this.playerAdapter) {
            this.playerAdapter.clear();
        }
        if (this.itemAdapter) {
            this.itemAdapter.clear();
        }
        if (this.laserAdapter) {
            this.laserAdapter.clear();
        }
        if (this.bossAdapter) {
            this.bossAdapter.clear();
        }
        if (this.bossDeathEffectAdapter) {
            this.bossDeathEffectAdapter.clear();
        }
        if (this.particleAdapter) {
            this.particleAdapter.clear();
        }
        if (this.pixiGameRenderer) {
            this.pixiGameRenderer.clear();
        }

        // 상태 초기화
        this.isGameOver = false;
        this.isPaused = false;
        this.showGameOverUI = false;
    }

    getScoreManager(): ScoreManager {
        return this.scoreManager;
    }
}
